<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import CampaignAiAgentFields from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignAiAgentFields.vue';
import {
  buildAudienceOptions,
  toAudiencePayload,
  audienceListValue,
} from 'dashboard/helper/campaignAudience';

const props = defineProps({
  presetAudienceListId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  audienceLists: useMapGetter('audienceLists/getAudienceLists'),
  inboxes: useMapGetter('inboxes/getEmailInboxes'),
};

const initialState = {
  title: '',
  subject: '',
  message: '',
  inboxId: null,
  aiAgentEnabled: false,
  aiPrompt: '',
  scheduledAt: null,
  selectedAudience: props.presetAudienceListId
    ? [audienceListValue(props.presetAudienceListId)]
    : [],
};

const state = reactive({ ...initialState });

const rules = {
  title: { required, minLength: minLength(1) },
  subject: { required, minLength: minLength(1) },
  // With the agent on there is no fixed body: it writes one per recipient.
  message: { required: requiredIf(() => !state.aiAgentEnabled) },
  aiPrompt: { required: requiredIf(() => state.aiAgentEnabled) },
  inboxId: { required },
  scheduledAt: { required },
  selectedAudience: { required },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const inboxOptions = computed(
  () =>
    formState.inboxes.value?.map(inbox => ({
      value: inbox.id,
      label: inbox.name,
    })) ?? []
);

const audienceOptions = computed(() =>
  buildAudienceOptions({
    labels: formState.labels.value,
    audienceLists: formState.audienceLists.value,
    listPrefixLabel: t('CAMPAIGN.AUDIENCE.OPTION_PREFIX'),
  })
);

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.EMAIL.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  subject: getErrorMessage('subject', 'SUBJECT'),
  message: getErrorMessage('message', 'MESSAGE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
  // Required once the agent is on. Without surfacing it the submit button was
  // simply disabled with nothing on screen to explain why.
  aiPrompt: v$.value.aiPrompt.$error ? t('CAMPAIGN.AI_AGENT.PROMPT.ERROR') : '',
}));

const isSubmitDisabled = computed(() => v$.value.$invalid);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, initialState);
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const aiPreviewContext = computed(() => ({
  title: state.title,
  message: state.message,
  inbox_id: state.inboxId,
  audience: toAudiencePayload(state.selectedAudience),
}));

// The subject travels in `template_params` because the campaigns table has no
// dedicated column for it; Email::OneoffCampaignService reads it back from there
// and stamps it on the conversation as `mail_subject`.
const prepareCampaignDetails = () => ({
  title: state.title,
  message: state.aiAgentEnabled ? '' : state.message,
  template_params: { subject: state.subject },
  ai_agent_enabled: state.aiAgentEnabled,
  ai_prompt: state.aiAgentEnabled ? state.aiPrompt : null,
  inbox_id: state.inboxId,
  scheduled_at: formatToUTCString(state.scheduledAt),
  audience: toAudiencePayload(state.selectedAudience),
});

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.EMAIL.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <Input
      v-model="state.subject"
      :label="t('CAMPAIGN.EMAIL.CREATE.FORM.SUBJECT.LABEL')"
      :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.SUBJECT.PLACEHOLDER')"
      :message="formErrors.subject"
      :message-type="formErrors.subject ? 'error' : 'info'"
    />

    <CampaignAiAgentFields
      v-model:enabled="state.aiAgentEnabled"
      v-model:prompt="state.aiPrompt"
      :preview-context="aiPreviewContext"
      :prompt-error="formErrors.aiPrompt"
    />

    <TextArea
      v-if="!state.aiAgentEnabled"
      v-model="state.message"
      :label="t('CAMPAIGN.EMAIL.CREATE.FORM.MESSAGE.LABEL')"
      :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.MESSAGE.PLACEHOLDER')"
      show-character-count
      :message="formErrors.message"
      :message-type="formErrors.message ? 'error' : 'info'"
    />
    <p v-if="!state.aiAgentEnabled" class="-mt-2 text-xs text-n-slate-11">
      {{ t('CAMPAIGN.EMAIL.CREATE.FORM.MESSAGE.INFO') }}
    </p>

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceOptions"
        :label="t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.EMAIL.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <div
      class="flex flex-col-reverse w-full gap-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.EMAIL.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.EMAIL.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
